import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Star, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { t, language, dir, getLocalizedField } = useLanguage();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/admin/seed`);
      
      const [servicesRes, testimonialsRes, galleryRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/gallery`),
      ]);
      setServices(servicesRes.data);
      setTestimonials(testimonialsRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        preferred_language: language,
      });
      toast.success(t('contact.success'));
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  return (
    <div className="noise-overlay" dir={dir}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1767050190883-29d644fa5b99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBldmVudCUyMGdhbGElMjB3ZWRkaW5nJTIwZWxlZ2FudHxlbnwwfHx8fDE3NzQ0NTY1MTB8MA&ixlib=rb-4.1.0&q=85"
            alt="Elegant event"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-dark/70 via-background-dark/50 to-background-dark/80" />
        </div>

        {/* Arabesque Pattern Overlay */}
        <div className="absolute inset-0 arabesque-pattern opacity-30" />

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="overline text-primary mb-6 animate-fade-in opacity-0" style={{ animationDelay: '200ms' }}>
            {t('hero.overline')}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-light text-white mb-4 tracking-tighter animate-fade-in opacity-0" style={{ animationDelay: '400ms' }}>
            {t('hero.title')}
          </h1>
          <p className="font-heading text-xl sm:text-2xl text-primary/90 mb-6 italic animate-fade-in opacity-0" style={{ animationDelay: '600ms' }}>
            {t('hero.subtitle')}
          </p>
          <p className="font-body text-base text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in opacity-0" style={{ animationDelay: '800ms' }}>
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in opacity-0" style={{ animationDelay: '1000ms' }}>
            <a href="#services" className="btn-primary flex items-center gap-2" data-testid="hero-cta-services">
              {t('hero.cta')}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#contact" className="btn-gold" data-testid="hero-cta-contact">
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-background-light" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline text-primary mb-4">{t('services.overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-text-primary tracking-tight mb-4">
              {t('services.title')}
            </h2>
            <p className="font-body text-text-muted max-w-xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <div
                key={service.id}
                className="group card-luxury overflow-hidden animate-fade-in opacity-0"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="img-hover-zoom aspect-[4/3]">
                  <img
                    src={service.image_url}
                    alt={getLocalizedField(service, 'title')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <p className="overline text-secondary mb-2">{service.category.toUpperCase()}</p>
                  <h3 className="font-heading text-xl font-medium text-text-primary mb-3">
                    {getLocalizedField(service, 'title')}
                  </h3>
                  <p className="font-body text-sm text-text-muted mb-4 line-clamp-2">
                    {getLocalizedField(service, 'description')}
                  </p>
                  <div className="mb-3">
                    <span className="font-body text-xs text-text-muted">
                      {t('services.from')} <span className="text-primary font-medium">{formatPrice(service.base_price)} XAF</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-3 border-t border-primary/10">
                    <Link
                      to={`/services/${service.id}`}
                      className="font-body text-sm font-medium text-text-muted hover:text-primary transition-colors flex items-center gap-1"
                      data-testid={`learn-more-${index}`}
                    >
                      {t('services.learnMore')}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                    <Link
                      to={`/booking?service=${service.id}`}
                      className="font-body text-sm font-medium text-primary hover:text-secondary transition-colors flex items-center gap-1"
                      data-testid={`book-service-${index}`}
                    >
                      {t('services.bookNow')}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-surface-light arabesque-pattern" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="overline text-primary mb-4">{t('testimonials.overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-text-primary tracking-tight">
              {t('testimonials.title')}
            </h2>
          </div>

          {testimonials.length > 0 && (
            <div className="relative">
              <div className="bg-white p-8 md:p-12 shadow-[0_20px_40px_-15px_rgba(197,131,140,0.15)] border border-primary/10">
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="font-body text-lg text-text-primary leading-relaxed mb-8 italic">
                  "{getLocalizedField(testimonials[currentTestimonial], 'content')}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading text-lg font-medium text-text-primary">
                      {testimonials[currentTestimonial]?.client_name}
                    </p>
                    <p className="font-body text-sm text-text-muted">
                      {testimonials[currentTestimonial]?.client_title}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={prevTestimonial}
                      className="p-2 border border-primary/20 hover:border-primary hover:bg-primary/5 transition-colors"
                      data-testid="testimonial-prev"
                    >
                      <ChevronLeft className="w-5 h-5 text-primary" />
                    </button>
                    <button
                      onClick={nextTestimonial}
                      className="p-2 border border-primary/20 hover:border-primary hover:bg-primary/5 transition-colors"
                      data-testid="testimonial-next"
                    >
                      <ChevronRight className="w-5 h-5 text-primary" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-padding bg-background-light" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline text-primary mb-4">{t('gallery.overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-text-primary tracking-tight">
              {t('gallery.title')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.slice(0, 6).map((item, index) => (
              <div
                key={item.id}
                className={`img-hover-zoom relative ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                data-testid={`gallery-item-${index}`}
              >
                <img
                  src={item.image_url}
                  alt={getLocalizedField(item, 'title')}
                  className={`w-full object-cover ${index === 0 ? 'h-[400px] md:h-full' : 'h-[200px] md:h-[250px]'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="font-body text-sm text-white">{getLocalizedField(item, 'title')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-surface-light" data-testid="contact-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left side - Info */}
            <div>
              <p className="overline text-primary mb-4">{t('contact.overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light text-text-primary tracking-tight mb-6">
                {t('contact.title')}
              </h2>
              <p className="font-body text-text-muted mb-8">
                {t('contact.subtitle')}
              </p>

              {/* Calendar Preview */}
              <div className="bg-white p-6 border border-primary/10 shadow-lg">
                <p className="font-body text-sm text-text-muted mb-4">{t('booking.selectDate')}</p>
                <Calendar
                  mode="single"
                  disabled={(date) => date < new Date()}
                  className="mx-auto"
                />
              </div>
            </div>

            {/* Right side - Form */}
            <div className="bg-white p-8 md:p-10 border border-primary/10 shadow-[0_20px_40px_-15px_rgba(197,131,140,0.15)]">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder={t('contact.name')}
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="contact-name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    placeholder={t('contact.email')}
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="contact-email"
                  />
                  <input
                    type="tel"
                    placeholder={t('contact.phone')}
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="input-flushed w-full"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder={t('contact.subject')}
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <textarea
                    placeholder={t('contact.message')}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    rows={4}
                    className="input-flushed w-full resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('contact.send')}
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
